import { Text } from "@mantine/core";
import type * as React from "react";

export type DataColumn<Row> = {
  key: string;
  header: string;
  render: (row: Row) => React.ReactNode;
};

/**
 * A table in a card (Figma "Table"): a `gray/1` header band with `gray/8` headings, 13px
 * rows divided by `border/default` lines inset by the card padding (panel.css). Columns
 * size to their content (Dan 2026-10-02). With no rows it shows `empty` in one dimmed row
 * under the headings.
 */
export function DataTable<Row>({
  columns,
  rows,
  rowKey,
  empty,
  label,
}: {
  columns: ReadonlyArray<DataColumn<Row>>;
  rows: readonly Row[];
  rowKey: (row: Row) => string;
  empty: string;
  /** The table's accessible name. */
  label: string;
}) {
  return (
    <div className="sb-table-card">
      <table className="sb-table" aria-label={label}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} scope="col">
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length}>
                <Text c="dimmed">{empty}</Text>
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr key={rowKey(row)}>
                {columns.map((c) => (
                  <td key={c.key}>{c.render(row)}</td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
